// notificationSounds.js - Web Audio API notification sounds
// With HTML5 Audio fallback for background tabs

let masterVolume = 0.3;
let audioContext = null;
let isInitialized = false;

// HTML5 Audio fallback elements (for background tabs)
const fallbackAudios = {};

/**
 * Get or create AudioContext
 */
const getAudioContext = () => {
    if (!audioContext) {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContext.state === 'suspended') {
        audioContext.resume();
    }
    return audioContext;
};

/**
 * Generate a WAV data URL for fallback audio
 * Matches Web Audio API envelope: linearRampToValueAtTime for attack, exponentialRampToValueAtTime for decay
 *
 * Each tone can specify:
 * - frequency: Hz
 * - duration: total duration in seconds
 * - volume: peak volume (0-1)
 * - startTime: when this tone starts
 * - attackTime: time to reach peak volume (linear ramp)
 * - decayTime: time from peak to ~0.001 (exponential decay)
 */
const generateWavDataUrl = (tones, sampleRate = 44100) => {
    let totalDuration = 0;
    tones.forEach(tone => {
        totalDuration = Math.max(totalDuration, (tone.startTime || 0) + tone.duration);
    });
    totalDuration += 0.05; // Small buffer at end

    const numSamples = Math.floor(sampleRate * totalDuration);
    const buffer = new ArrayBuffer(44 + numSamples * 2);
    const view = new DataView(buffer);

    const writeString = (offset, string) => {
        for (let i = 0; i < string.length; i++) {
            view.setUint8(offset + i, string.charCodeAt(i));
        }
    };

    // WAV header
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + numSamples * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, numSamples * 2, true);

    // Generate samples
    for (let i = 0; i < numSamples; i++) {
        const t = i / sampleRate;
        let sample = 0;

        tones.forEach(tone => {
            const toneStart = tone.startTime || 0;
            const toneEnd = toneStart + tone.duration;

            if (t >= toneStart && t < toneEnd) {
                const toneT = t - toneStart;
                const attackTime = tone.attackTime || 0.05;
                const decayStart = attackTime;
                const decayDuration = tone.decayTime || (tone.duration - attackTime);

                let envelope;
                if (toneT < attackTime) {
                    // Linear ramp from 0 to volume (matches linearRampToValueAtTime)
                    envelope = (toneT / attackTime) * tone.volume;
                } else {
                    // Exponential decay from volume to 0.001 (matches exponentialRampToValueAtTime)
                    // exponentialRampToValueAtTime goes from current value to target over time
                    // We need to calculate the decay rate to reach 0.001 at decayDuration
                    const decayT = toneT - decayStart;
                    // Formula: value = startValue * (endValue/startValue)^(t/duration)
                    // Simplified for endValue = 0.001: value = startValue * 0.001^(t/duration)
                    // But more accurately: value = startValue * exp(-k * t) where k = ln(startValue/0.001)/duration
                    const k = Math.log(tone.volume / 0.001) / decayDuration;
                    envelope = tone.volume * Math.exp(-k * decayT);
                }

                sample += Math.sin(2 * Math.PI * tone.frequency * toneT) * envelope;
            }
        });

        sample = Math.max(-1, Math.min(1, sample));
        view.setInt16(44 + i * 2, Math.floor(sample * 32767), true);
    }

    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return 'data:audio/wav;base64,' + btoa(binary);
};

// Sound definitions - matching exact Web Audio API parameters
// Each tone specifies attackTime and decayTime to match the Web Audio envelope
const soundDefs = {
    // Agent: ascending two-tone chime (C5, E5) - attack 0.05s, decay to 0.4s total
    agent: [
        { frequency: 523.25, duration: 0.5, volume: 0.4, startTime: 0, attackTime: 0.05, decayTime: 0.35 },
        { frequency: 659.25, duration: 0.5, volume: 0.4, startTime: 0.12, attackTime: 0.05, decayTime: 0.35 }
    ],
    // User Chat: single ding at C6 - very fast attack (0.008s), long decay (0.6s)
    userChat: [
        { frequency: 1047, duration: 0.65, volume: 0.15, startTime: 0, attackTime: 0.008, decayTime: 0.592 }
    ],
    // Clara/Success: ascending C major arpeggio (C5, E5, G5) - attack 0.03s, decay to 0.35s
    clara: [
        { frequency: 523.25, duration: 0.4, volume: 0.35, startTime: 0, attackTime: 0.03, decayTime: 0.32 },
        { frequency: 659.25, duration: 0.4, volume: 0.35, startTime: 0.08, attackTime: 0.03, decayTime: 0.32 },
        { frequency: 783.99, duration: 0.4, volume: 0.35, startTime: 0.16, attackTime: 0.03, decayTime: 0.32 }
    ],
    // Invitation: attention-getting (F5, A5) - attack 0.04s, decay to 0.35s
    invitation: [
        { frequency: 698.46, duration: 0.4, volume: 0.35, startTime: 0, attackTime: 0.04, decayTime: 0.31 },
        { frequency: 880.00, duration: 0.4, volume: 0.35, startTime: 0.1, attackTime: 0.04, decayTime: 0.31 }
    ],
    // Error: descending two tones (F4, D4) - attack 0.04s, decay to 0.3s
    error: [
        { frequency: 349.23, duration: 0.35, volume: 0.3, startTime: 0, attackTime: 0.04, decayTime: 0.26 },
        { frequency: 293.66, duration: 0.35, volume: 0.3, startTime: 0.15, attackTime: 0.04, decayTime: 0.26 }
    ],
    // Success: same as Clara
    success: [
        { frequency: 523.25, duration: 0.4, volume: 0.35, startTime: 0, attackTime: 0.03, decayTime: 0.32 },
        { frequency: 659.25, duration: 0.4, volume: 0.35, startTime: 0.08, attackTime: 0.03, decayTime: 0.32 },
        { frequency: 783.99, duration: 0.4, volume: 0.35, startTime: 0.16, attackTime: 0.03, decayTime: 0.32 }
    ],
    // Subtle: minimal ping - very fast attack (0.005s), quick decay (0.2s)
    subtle: [
        { frequency: 880, duration: 0.25, volume: 0.1, startTime: 0, attackTime: 0.005, decayTime: 0.195 }
    ]
};

/**
 * Initialize audio - call from user gesture
 */
export const initializeAudio = async () => {
    if (isInitialized) return;

    console.log('🔊 Initializing audio...');

    // Initialize Web Audio API
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
        await ctx.resume();
    }

    // Pre-create and prime HTML5 Audio fallbacks
    for (const [type, tones] of Object.entries(soundDefs)) {
        const dataUrl = generateWavDataUrl(tones);
        fallbackAudios[type] = new Audio(dataUrl);
        fallbackAudios[type].volume = masterVolume;

        // Prime by playing silently
        try {
            fallbackAudios[type].volume = 0.001;
            await fallbackAudios[type].play();
            fallbackAudios[type].pause();
            fallbackAudios[type].currentTime = 0;
            fallbackAudios[type].volume = masterVolume;
        } catch (e) {
            console.warn(`Could not prime ${type} audio`);
        }
    }

    isInitialized = true;
    console.log('🔊 Audio initialized, Web Audio state:', ctx.state);
};

/**
 * Play sound using Web Audio API (your exact implementation)
 */
const playWithWebAudio = (type) => {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const tones = soundDefs[type];

    if (type === 'agent') {
        // AI Assistant - ascending two-tone chime (C5, E5)
        [523.25, 659.25].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, now);
            const start = now + i * 0.12;
            gain.gain.setValueAtTime(0, start);
            gain.gain.linearRampToValueAtTime(masterVolume * 0.4, start + 0.05);
            gain.gain.exponentialRampToValueAtTime(0.001, start + 0.4);
            osc.connect(gain).connect(ctx.destination);
            osc.start(start);
            osc.stop(start + 0.5);
        });
    } else if (type === 'userChat') {
        // User Chat - single ding at C6 with 0.6s decay
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(1047, now);
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(masterVolume * 0.15, now + 0.008);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.65);
    } else if (type === 'clara' || type === 'success') {
        // Clara/Success - ascending C major arpeggio (C5, E5, G5)
        [523.25, 659.25, 783.99].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, now);
            const start = now + i * 0.08;
            gain.gain.setValueAtTime(0, start);
            gain.gain.linearRampToValueAtTime(masterVolume * 0.35, start + 0.03);
            gain.gain.exponentialRampToValueAtTime(0.001, start + 0.35);
            osc.connect(gain).connect(ctx.destination);
            osc.start(start);
            osc.stop(start + 0.4);
        });
    } else if (type === 'invitation') {
        // Invitation - attention-getting (F5, A5)
        [698.46, 880.00].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, now);
            const start = now + i * 0.1;
            gain.gain.setValueAtTime(0, start);
            gain.gain.linearRampToValueAtTime(masterVolume * 0.35, start + 0.04);
            gain.gain.exponentialRampToValueAtTime(0.001, start + 0.35);
            osc.connect(gain).connect(ctx.destination);
            osc.start(start);
            osc.stop(start + 0.4);
        });
    } else if (type === 'error') {
        // Error - descending two tones (F4, D4)
        [349.23, 293.66].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(freq, now);
            const start = now + i * 0.15;
            gain.gain.setValueAtTime(0, start);
            gain.gain.linearRampToValueAtTime(masterVolume * 0.3, start + 0.04);
            gain.gain.exponentialRampToValueAtTime(0.001, start + 0.3);
            osc.connect(gain).connect(ctx.destination);
            osc.start(start);
            osc.stop(start + 0.35);
        });
    } else if (type === 'subtle') {
        // Subtle - minimal ping
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, now);
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(masterVolume * 0.1, now + 0.005);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.25);
    }
};

/**
 * Play sound with HTML5 Audio fallback
 */
const playWithFallback = (type) => {
    const audio = fallbackAudios[type];
    if (audio) {
        audio.volume = masterVolume;
        audio.currentTime = 0;
        audio.play().catch(err => {
            console.warn(`Fallback audio failed for ${type}:`, err.message);
        });
    }
};

/**
 * Play a sound - tries Web Audio API first, falls back to HTML5 Audio
 */
const playSound = (type) => {
    try {
        // If document is not visible (background window/tab), always use HTML5 Audio
        // Web Audio API often doesn't play reliably when window is inactive
        if (document.visibilityState !== 'visible') {
            playWithFallback(type);
            console.log(`🔔 Playing ${type} notification (HTML5 - background window)`);
            return;
        }

        const ctx = getAudioContext();

        // If AudioContext is running and document is visible, use Web Audio API
        if (ctx.state === 'running') {
            playWithWebAudio(type);
            console.log(`🔔 Playing ${type} notification (Web Audio)`);
        } else {
            // Fall back to HTML5 Audio (works in background tabs if primed)
            playWithFallback(type);
            console.log(`🔔 Playing ${type} notification (HTML5 fallback)`);
        }
    } catch (err) {
        // Last resort fallback
        playWithFallback(type);
        console.log(`🔔 Playing ${type} notification (fallback after error)`);
    }
};

/**
 * Set master volume (0-1)
 */
export const setMasterVolume = (volume) => {
    masterVolume = Math.max(0, Math.min(1, volume));
    Object.values(fallbackAudios).forEach(audio => {
        audio.volume = masterVolume;
    });
};

/**
 * Get current master volume
 */
export const getMasterVolume = () => masterVolume;

// Export notification functions
export const playAgentNotification = () => playSound('agent');
export const playUserChatNotification = () => playSound('userChat');
export const playClaraNotification = () => playSound('clara');
export const playInvitationNotification = () => playSound('invitation');
export const playErrorNotification = () => playSound('error');
export const playSuccessNotification = () => playSound('success');
export const playSubtleNotification = () => playSound('subtle');

export default {
    initializeAudio,
    playAgentNotification,
    playUserChatNotification,
    playClaraNotification,
    playInvitationNotification,
    playErrorNotification,
    playSubtleNotification,
    playSuccessNotification,
    setMasterVolume,
    getMasterVolume
};
